import React, { useCallback, useEffect, useState } from "react";
import { createLogger } from "debug/DebugingLogger";
import {
  fetchOrgMembers,
  preflightCheckout,
  type CatalogPlan,
  type ContactPreviewOutcome,
  type CurrentPlan,
  type Org,
} from "@/lib/api/organizations";
import {
  isCheckoutConfigured,
  previewPrices,
  priceIdFor,
  QUOTE_SETTLE_MS,
  type BillingInterval,
  type CheckoutRequest,
  type LivePrice,
} from "@/lib/paddle";
import { useAuth } from "contexts/AuthProvider";
import { useSupportStore } from "stores/useSupportStore";
import { confirmPlanChange } from "@/stores/usePendingCheckoutStore";
import { useDialog, type Dialog } from "components/hooks/useDialog";
import { PlansDialog } from "components/main/organizations/PlansDialog";
import { CheckoutDialog } from "components/main/organizations/CheckoutDialog";
import { BillingEmailDialog } from "components/main/organizations/BillingEmailDialog";

const log = createLogger("PlanUpgrade");

function refusal(reason: string | null): string {
  switch (reason) {
    case "subscribed":
      return "This organization already holds a paid subscription. Nothing was charged.";
    case "restored":
      return "We found the subscription you already pay for and restored it. Nothing was charged.";
    case "unattached":
      return "A subscription billed to your address isn't attached to any organization. Attach it above if you bought it, or cancel it in the billing portal — nothing was charged.";
    case "unverified":
      return "Verify your email address before subscribing. Nothing was charged.";
    case "paid_org_limit":
      return "An owner of this organization already owns the most paid organizations one account may. Nothing was charged.";
    default:
      return "We couldn't confirm your payment details, so nothing was charged. Please try again in a moment.";
  }
}

/// The plans dialog and the checkout it opens. The checkout outlives the plans
/// dialog, which is withheld while a payment is still landing.
export const PlanUpgrade: React.FC<{
  org: Org;
  plan: CurrentPlan | null;
  catalog: CatalogPlan[];
  upgrades: CatalogPlan[];
  currentRow: CatalogPlan | null;
  memberCount: number;
  plans: Dialog;
  plansOffered: boolean;
  checkAddress: (email: string) => Promise<ContactPreviewOutcome | null>;
  onRefused: () => void;
}> = ({
  org,
  plan,
  catalog,
  upgrades,
  currentRow,
  memberCount,
  plans,
  plansOffered,
  checkAddress,
  onRefused,
}) => {
  const { email } = useAuth();
  const openSupport = useSupportStore((s) => s.openSupport);
  const [interval, setBillingInterval] = useState<BillingInterval>("month");
  // The occupied count is a floor; derived, since `memberCount` arrives after mount.
  const [chosenSeats, setChosenSeats] = useState(1);
  const seatFloor = org.is_personal ? 1 : Math.max(memberCount, 1);
  const seats = org.is_personal ? 1 : Math.max(chosenSeats, seatFloor);
  const selection = `${interval}:${seats}`;
  const [quote, setQuote] = useState<{
    selection: string;
    prices: Record<string, LivePrice>;
  }>();
  const [busyCode, setBusyCode] = useState<string | null>(null);
  const checkout = useDialog<{ name: string; request: CheckoutRequest }>();
  /// `open` runs the checkout once the buyer has picked a billing address.
  const emailPrompt = useDialog<{
    accountEmail: string | null;
    accountEmailTaken: boolean;
    open: (billingEmail?: string) => void;
  }>();
  const { setError: setCheckoutError, close: closePlans } = plans;
  const { show: showCheckout, close: closeCheckout } = checkout;
  const { show: showEmailPrompt } = emailPrompt;
  const prompt = emailPrompt.payload;

  // `is_purchasable` is the catalog's say; the env price id is only whether this
  // build can transact it.
  const buyable = upgrades.filter(
    (row) =>
      row.is_purchasable &&
      isCheckoutConfigured() &&
      priceIdFor(row.code, interval),
  );

  // A term with no price id behind it would be a dead switch.
  const offersYearly = (code: string): boolean =>
    Boolean(
      catalog.some((row) => row.code === code && row.price_cents_yearly) &&
      isCheckoutConfigured() &&
      priceIdFor(code, "year"),
    );

  const comparison: { tier: CatalogPlan; state: "current" | "upgrade" }[] = [
    ...(currentRow ? [{ tier: currentRow, state: "current" as const }] : []),
    ...upgrades.map((tier) => ({ tier, state: "upgrade" as const })),
  ];

  useEffect(() => {
    const ids = buyable
      .map((row) => priceIdFor(row.code, interval))
      .filter((id): id is string => Boolean(id));
    if (ids.length === 0) return;
    let cancelled = false;
    const settle = (prices: Record<string, LivePrice>) => {
      if (!cancelled) setQuote({ selection, prices });
    };
    const timer = setTimeout(() => {
      previewPrices(ids, seats)
        .then((quotes) =>
          settle(
            Object.fromEntries(
              buyable.flatMap((row) => {
                const id = priceIdFor(row.code, interval);
                const price = id ? quotes[id] : undefined;
                return price ? [[row.code, price]] : [];
              }),
            ),
          ),
        )
        .catch((e: unknown) => {
          log.error("price preview failed", e);
          // Paddle is the one source: a failed quote shows no figure.
          settle({});
        });
    }, QUOTE_SETTLE_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, interval, seats]);

  // A quote answers one term and seat count; the card waits for the current one.
  const livePrices = Object.fromEntries(
    buyable.flatMap((row): [string, LivePrice | "quoting"][] => {
      if (quote?.selection !== selection) return [[row.code, "quoting"]];
      const price = quote.prices[row.code];
      return price ? [[row.code, price]] : [];
    }),
  );

  const subscribe = useCallback(
    async (code: string) => {
      const priceId = priceIdFor(code, interval);
      // A second press would tear the first checkout down mid-open.
      if (!priceId || busyCode !== null) return;
      setBusyCode(code);
      setCheckoutError(null);
      try {
        // Re-read inside the click: the roster can have grown since mount.
        if (!org.is_personal) {
          const occupied = (await fetchOrgMembers(org.org_id)).length;
          if (seats < occupied) {
            setCheckoutError(
              `This organization now holds ${occupied} members and outstanding invitations. Choose at least ${occupied} seats.`,
            );
            setBusyCode(null);
            return;
          }
        }
        const cleared = await preflightCheckout(org.org_id);
        // No token is a refusal even on a "go": attribution would rest on the hint.
        if (!cleared.may_check_out || !cleared.checkout_token) {
          setCheckoutError(
            refusal(cleared.may_check_out ? "unreachable" : cleared.reason),
          );
          setBusyCode(null);
          onRefused();
          return;
        }
        // Narrowing does not survive into the closure below.
        const token = cleared.checkout_token;
        const from = plan?.code ?? "";
        const open = (billingEmail?: string) =>
          showCheckout({
            name: catalog.find((row) => row.code === code)?.name ?? "",
            request: {
              priceId,
              quantity: seats,
              // Never a customer id: Paddle files the checkout under the
              // customer at this email, and the pre-flight named that one.
              email: billingEmail ?? email ?? undefined,
              onCompleted: () => {
                closeCheckout();
                confirmPlanChange(org.org_id, from);
              },
              customData: {
                org_id: org.org_id,
                plan_code: code,
                checkout_token: token,
                org_kind: org.is_personal ? "personal" : "company",
                org_name: org.name,
              },
            },
          });
        showEmailPrompt({
          accountEmail: email ?? null,
          accountEmailTaken: cleared.needs_billing_email ?? false,
          open,
        });
        setBusyCode(null);
        return;
      } catch (e: unknown) {
        log.error("checkout could not be prepared", e);
        setCheckoutError("Checkout could not be opened. Please try again.");
        setBusyCode(null);
      }
    },
    [
      busyCode,
      catalog,
      email,
      interval,
      onRefused,
      seats,
      org.org_id,
      org.is_personal,
      org.name,
      plan?.code,
      setCheckoutError,
      showCheckout,
      closeCheckout,
      showEmailPrompt,
    ],
  );

  return (
    <>
      {prompt && (
        <BillingEmailDialog
          open
          accountEmail={prompt.accountEmail}
          accountEmailTaken={prompt.accountEmailTaken}
          checkAddress={checkAddress}
          onCancel={emailPrompt.close}
          onConfirm={(billingEmail) => {
            emailPrompt.close();
            prompt.open(billingEmail);
          }}
        />
      )}
      <CheckoutDialog
        request={checkout.payload?.request ?? null}
        planName={checkout.payload?.name ?? ""}
        onClose={() => {
          closeCheckout();
          setBusyCode(null);
        }}
        onSettled={() => {
          setBusyCode(null);
          closePlans();
        }}
      />
      {plansOffered && (
        <PlansDialog
          open={plans.open}
          onClose={closePlans}
          plans={comparison}
          interval={interval}
          onIntervalChange={setBillingInterval}
          offersYearly={offersYearly}
          seats={seats}
          minSeats={seatFloor}
          onSeatsChange={org.is_personal ? undefined : setChosenSeats}
          livePrices={livePrices}
          canBuy={(code) => buyable.some((row) => row.code === code)}
          onSubscribe={(code) => void subscribe(code)}
          busyCode={busyCode}
          error={plans.error}
          onContactSales={(anchorTop) =>
            openSupport(anchorTop, {
              subject: "Enterprise plan",
              label: "enterprise",
            })
          }
        />
      )}
    </>
  );
};
